import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Shield, ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui-bits";

export default function RolesPanel() {
  const [rows, setRows] = useState([]);
  const [registry, setRegistry] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ key: "", label: "", permissions: [] });

  const load = async () => setRows((await api.get("/roles")).data);
  const loadRegistry = async () => {
    try {
      const { data } = await api.get("/users/permissions");
      setRegistry(data.permissions || []);
    } catch {
      setRegistry([]);
    }
  };
  useEffect(() => {
    load();
    loadRegistry();
  }, []);

  const startNew = () => {
    setEditing(null);
    setForm({ key: "", label: "", permissions: [] });
    setOpen(true);
  };
  const startEdit = (r) => {
    setEditing(r);
    setForm({ key: r.key, label: r.label, permissions: r.permissions || [] });
    setOpen(true);
  };
  const togglePerm = (k) =>
    setForm((f) => ({
      ...f,
      permissions: f.permissions.includes(k)
        ? f.permissions.filter((p) => p !== k)
        : [...f.permissions, k],
    }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const body = { label: form.label };
        if (editing.key !== "admin") body.permissions = form.permissions;
        await api.patch(`/roles/${editing.id}`, body);
      } else {
        await api.post("/roles", form);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (r) => {
    if (r.is_system) {
      toast.error("System roles cannot be deleted.");
      return;
    }
    if (!confirm(`Delete role "${r.label}"? Any users on this role will need to be reassigned first.`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      toast.success("Role deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Panel
      title={`Roles (${rows.length})`}
      action={
        <Button className="h-11 gap-2" onClick={startNew} data-testid="new-role-button">
          <Plus className="h-4 w-4" /> New role
        </Button>
      }
    >
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="roles-table">
          <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="py-3">Label</th>
              <th>Key</th>
              <th>Permissions</th>
              <th>Users</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-t border-border h-16"
                data-testid={`role-row-${r.key}`}
              >
                <td className="font-medium">{r.label}</td>
                <td><span className="font-mono text-xs text-stone-500">{r.key}</span></td>
                <td className="text-sm">
                  {r.key === "admin" ? (
                    <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
                      <ShieldCheck className="h-3.5 w-3.5" /> All tabs (system)
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-stone-700">
                      <Shield className="h-3.5 w-3.5" />
                      {(r.permissions || []).length} / {registry.length}
                    </span>
                  )}
                </td>
                <td className="text-sm text-stone-600 tnum">{r.user_count ?? 0}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => startEdit(r)}
                      data-testid={`edit-role-${r.key}`}
                      title="Edit role"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(r)}
                      data-testid={`delete-role-${r.key}`}
                      disabled={r.is_system}
                      title={r.is_system ? "System role" : "Delete role"}
                    >
                      <Trash2
                        className={`h-4 w-4 ${r.is_system ? "text-stone-300" : "text-rose-600"}`}
                      />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="role-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit role" : "New role"}
              {editing?.is_system && (
                <span className="ml-2 text-xs font-normal text-stone-500">(system)</span>
              )}
            </DialogTitle>
            <DialogDescription>
              {editing?.key === "admin"
                ? "The admin role's permissions cover every tab and aren't editable."
                : "Tick the tabs this role unlocks. Users created with this role inherit these permissions by default — admin can still override per-user from the Users tab."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Label</Label>
              <Input
                className="h-12 mt-1"
                required
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                data-testid="role-label"
                placeholder="Cleaner, Driver, Bakery Manager…"
              />
            </div>
            <div>
              <Label>Key</Label>
              <Input
                className="h-12 mt-1 font-mono"
                required
                value={form.key}
                onChange={(e) =>
                  setForm({ ...form, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
                }
                data-testid="role-key"
                placeholder="cleaner"
                disabled={!!editing}
              />
              <p className="text-[10px] text-stone-500 mt-1">
                Used internally (immutable). Letters / digits / underscore.
              </p>
            </div>
            {editing?.key !== "admin" && (
              <div>
                <Label>Permissions</Label>
                <div className="mt-2 grid grid-cols-1 gap-1.5 max-h-[40vh] overflow-y-auto pr-1">
                  {registry.map((p) => {
                    const checked = form.permissions.includes(p.key);
                    return (
                      <label
                        key={p.key}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                          checked ? "border-amber-300 bg-amber-50" : "border-border hover:bg-stone-50"
                        }`}
                        data-testid={`role-perm-${p.key}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePerm(p.key)}
                          className="h-5 w-5 rounded border-stone-300 text-amber-600 focus:ring-amber-400"
                          data-testid={`role-perm-check-${p.key}`}
                        />
                        <span className="text-sm font-medium text-stone-700">{p.label}</span>
                        <span className="ml-auto text-[10px] font-mono text-stone-400">{p.key}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="flex gap-3 mt-2 text-xs text-stone-500">
                  <button
                    type="button"
                    className="hover:underline underline-offset-2"
                    onClick={() => setForm((f) => ({ ...f, permissions: registry.filter((p) => p.key !== "admin").map((p) => p.key) }))}
                    data-testid="role-perm-select-all"
                  >
                    Select all (no admin)
                  </button>
                  <button
                    type="button"
                    className="hover:underline underline-offset-2"
                    onClick={() => setForm((f) => ({ ...f, permissions: [] }))}
                    data-testid="role-perm-clear"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="h-12 px-6" data-testid="role-submit">
                Save role
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
